import dayjs from 'dayjs';
import '@/styles/common/Preview/preview.scss';

import React, { VFC } from 'react';

import { ResumePreviewPage1 } from '@/components/Resume/Preview/Page1';
import { ResumePreviewPage2 } from '@/components/Resume/Preview/Page2';
import {
  CareerSummaryModel,
  EducationData,
  MBResumeResponse,
  Qualification,
} from '@/utils/api-client/api';

export type TFetchDataProps = {
  fetchData: MBResumeResponse;
};

type THistoryData = {
  educations1: EducationData[];
  educations2: EducationData[];
  careers1: CareerSummaryModel[];
  careers2: CareerSummaryModel[];
  qualifications: Qualification[];
};

/**
 * # 入力代行履歴書 プレビュー /mypage/resume/confirmation
 */

export const PagesResumeConfirmationPreview: VFC<{ resumeData?: MBResumeResponse }> = ({
  resumeData,
}): React.ReactElement => {
  const historyData: THistoryData = {
    educations1: [],
    educations2: [],
    careers1: [],
    careers2: [],
    qualifications: [],
  };

  /**
   * 職歴の入社と退社を分ける
   */
  const separateJoinAndLeave = (data: CareerSummaryModel[], careerLength: number) => {
    if (data.length === 0) return [];
    const careers = [];
    const reverseFlag =
      data.length === 1
        ? true
        : data[0].c_join_date < data[data.length - 1].c_join_date
        ? true
        : false;
    if (reverseFlag) data.reverse();
    const leaveCount = data.filter((career) => career.c_leave_flag).length;
    for (let i = 0; careers.length < Math.min(careerLength, data.length + leaveCount); i++) {
      careers.push(data[i]);
      if (careers.length < careerLength && data[i].c_leave_flag) {
        careers.push(Object.assign({ isJoin: true }, careersData[i]));
      }
    }
    if (reverseFlag) careers.reverse();
    return careers;
  };

  // const resumeData = usePageResumeFetch(type);

  const educationsData = resumeData?.educations ? Array.from(resumeData?.educations.values()) : [];
  const careersData = resumeData?.careers ? Array.from(resumeData?.careers.values()) : [];
  const qualificationsData = resumeData?.qualifications
    ? Array.from(resumeData?.qualifications.values())
    : [];

  if (educationsData.length >= 25) {
    // 学歴が25件以上の場合
    const educations = educationsData.slice(-25);
    historyData.educations1 = educations.splice(0, 16);
    historyData.educations2 = educations;
  } else if (educationsData.length >= 16) {
    // 学歴が16件以上の場合(2ページ目にまたぐ)
    historyData.educations1 = educationsData.splice(0, 16);
    historyData.educations2 = educationsData;
    const careerLength = 9 - historyData.educations2.length;
    const careers = separateJoinAndLeave(careersData, careerLength);
    historyData.careers2 = careers;
  } else {
    // 学歴が16件未満の場合(1ページ目に収まる)
    historyData.educations1 = educationsData;
    const careerLength = 25 - historyData.educations1.length;
    const careers = separateJoinAndLeave(careersData, careerLength);
    historyData.careers1 = careers.splice(0, 16 - historyData.educations1.length);
    historyData.careers2 = careers;
  }
  if (
    historyData.educations2.length + historyData.careers2.length === 0 &&
    historyData.educations1.length < 16
  ) {
    // 1ページ目に学歴、職歴がおさまる場合
    historyData.qualifications = qualificationsData.splice(-10);
  } else if (historyData.educations2.length + historyData.careers2.length < 9) {
    // 2ページ目にまたぐが資格・免許を表示する余地がある場合
    historyData.qualifications = qualificationsData.splice(
      historyData.educations2.length + historyData.careers2.length - 9
    );
  }

  const today = dayjs().format('YYYY-MM-DD');
  const date = today
    ? `${today.split('-')[0]}年${today.split('-')[1]}月${today.split('-')[2]}日`
    : '';

  return (
    <div className="mbx-preview mbx-utils-stack-v--preview" id="PREVIEW_RESUME">
      <ResumePreviewPage1
        date={date}
        fetchData={resumeData}
        educations={historyData.educations1}
        careers={historyData.careers1}
        secondPageCareersItemLength={historyData.careers2.length}
      />
      <ResumePreviewPage2
        date={date}
        fetchData={resumeData}
        educations={historyData.educations2}
        careers={historyData.careers2}
        qualifications={historyData.qualifications}
        firstPageEducationsItemLength={historyData.educations1.length}
      />
    </div>
  );
};
